/* =============================================================================
   VIEWS / VIEW-SWITCH — View + Tab Routing
   =============================================================================
   - switchView: customer | admin | cancel | kds (Session 19, Phase 15)
   - switchTab:  Admin-Tabs (Dashboard, Reservierungen, ...)
   - showAdminPanel: Login-Form / Admin-Panel Toggle, Tab-Wechsel nur beim
     Erst-Einblenden (Schutz gegen Token-Refresh-Reset, Session 6 Bugfix)
   - checkAdminAccessFromUrl + bindAdminShortcut: Cmd+Shift+A + Hashchange
============================================================================ */

function callIfFn(name, ...args) {
  if (typeof window[name] === 'function') window[name](...args);
}

/**
 * S209-HF2 (1) — Fahrer-Deep-Link-Gate (pure, für Vitest). Ein Operator mit
 * PRIMÄR-Rolle 'driver' (reiner Fahrer) landet bei JEDEM Admin-Einstieg auf
 * seinem Terminal — egal ob per PIN-Login-Auto-Einstieg, Topbar oder direktem
 * `#admin/...`-Hash. Multi-Role (Primär ≠ driver, z.B. manager+driver) und alle
 * anderen Rollen bleiben unberührt → kein View-Hijack.
 */
export function shouldRedirectDriverToTerminal(view, operatorRole) {
  return view === 'admin' && operatorRole === 'driver';
}

function _liveOperatorRole() {
  try {
    const api = window.App && window.App.operator;
    const op = (api && typeof api.getOperator === 'function') ? api.getOperator() : null;
    return op && op.role;
  } catch (_) { return null; }
}

export function switchView(view) {
  // S209-HF2 (1) — zentrales Fahrer-Deep-Link-Gate VOR jeder View-Logik: reiner
  // Fahrer → immer aufs Terminal umleiten (Server ist ohnehin dicht, das hier
  // ist reines UI-Gating).
  if (shouldRedirectDriverToTerminal(view, _liveOperatorRole())) {
    view = 'driver';
  }
  const vc = document.getElementById('view-customer');
  const va = document.getElementById('view-admin');
  const vx = document.getElementById('view-cancel');
  const vk = document.getElementById('view-kds');
  const vb = document.getElementById('view-bar');
  const vd = document.getElementById('view-delivery');
  const vt = document.getElementById('view-tracking');
  const vf = document.getElementById('view-driver');  // S209 — Fahrer-Terminal
  const vnl = document.getElementById('view-newsletter'); // S226 — Newsletter-Abo (public)
  const vtb = document.getElementById('view-table');      // S244 — Tisch-QR (public)
  const vsc = document.getElementById('view-schedule');   // S262 — Dienstplan (public, Token-Link)
  // NOOP-Schutz (Session 13.1): Wenn die gewuenschte View bereits aktiv
  // ist, nicht neu zeichnen — sonst gehen User-Eingaben (Form-Werte,
  // Scroll-Position, offene Modals) verloren, wenn ein anderer Code-Pfad
  // switchView() redundant aufruft (Token-Refresh, hashchange, init).
  const currentlyAdmin    = va && va.style.display === 'block';
  const currentlyCustomer = vc && vc.style.display === 'block';
  const currentlyCancel   = vx && vx.style.display === 'block';
  const currentlyKds      = vk && vk.style.display === 'block';
  const currentlyBar      = vb && vb.style.display === 'block';
  const currentlyDelivery = vd && vd.style.display === 'block';
  const currentlyTracking = vt && vt.style.display === 'block';
  const currentlyDriver   = vf && vf.style.display === 'block';
  const currentlyNewsletter = vnl && vnl.style.display === 'block';
  const currentlyTable      = vtb && vtb.style.display === 'block';
  const currentlySchedule   = vsc && vsc.style.display === 'block';
  if ((view === 'admin'    && currentlyAdmin)    ||
      (view === 'schedule' && currentlySchedule) ||
      (view === 'customer' && currentlyCustomer) ||
      (view === 'cancel'   && currentlyCancel)   ||
      (view === 'kds'      && currentlyKds)      ||
      (view === 'bar'      && currentlyBar)      ||
      (view === 'delivery' && currentlyDelivery) ||
      (view === 'tracking' && currentlyTracking) ||
      (view === 'newsletter' && currentlyNewsletter) ||
      (view === 'table'      && currentlyTable)      ||
      (view === 'driver'   && currentlyDriver)) {
    return;
  }
  // S208 — beim TATSÄCHLICHEN View-Wechsel offene Overlay-Modals des verlassenen
  // Kontexts aufräumen: das Gast-Wunschtisch-Popup (eigenes #cust-plan-modal) UND das
  // geteilte Admin-Modal-Overlay (closeModal). Läuft nur NACH dem NOOP-Guard → keine
  // redundanten Token-Refresh-/hashchange-Aufrufe schließen versehentlich ein Modal.
  // Kein View-Hijack — nur Aufräumen beim Verlassen.
  callIfFn('custPlanCloseModal');
  callIfFn('closeModal');
  // KDS/Bar/Delivery/Fahrer-Terminal erfordern Auth — sonst fallback auf admin (Login-Form).
  if (view === 'kds' || view === 'bar' || view === 'delivery' || view === 'driver') {
    const isAuthed = window.App && window.App.auth && window.App.auth.isAuthenticated();
    if (!isAuthed) view = 'admin';
  }
  if (vc) vc.style.display = view === 'customer' ? 'block' : 'none';
  if (va) va.style.display = view === 'admin'    ? 'block' : 'none';
  if (vx) vx.style.display = view === 'cancel'   ? 'block' : 'none';
  if (vk) vk.style.display = view === 'kds'      ? 'block' : 'none';
  if (vb) vb.style.display = view === 'bar'      ? 'block' : 'none';
  if (vd) vd.style.display = view === 'delivery' ? 'block' : 'none';
  if (vt) vt.style.display = view === 'tracking' ? 'block' : 'none';
  if (vf) vf.style.display = view === 'driver'   ? 'block' : 'none';
  if (vnl) vnl.style.display = view === 'newsletter' ? 'block' : 'none';
  if (vtb) vtb.style.display = view === 'table'      ? 'block' : 'none';
  if (vsc) vsc.style.display = view === 'schedule'   ? 'block' : 'none';
  // S168 — Dark ist global (:root), keine View-abhängige body-Klasse mehr nötig.
  // Tab-Title je nach View aktualisieren (Bugfix Session 22 Funktionstest:
  // bisher blieb der Title statisch auf "Grill & Green – Reservierung",
  // auch im Admin-Cockpit, KDS und Theken-Display).
  try {
    const titleMap = {
      customer: 'Grill & Green – Reservierung',
      admin:    'Grill & Green – Admin-Cockpit',
      cancel:   'Grill & Green – Stornierung',
      kds:      'Grill & Green – Küchen-Display',
      bar:      'Grill & Green – Theken-Display',
      delivery: 'Grill & Green – Liefer-Cockpit',
      tracking: 'Grill & Green – Bestellstatus',
      driver:   'Grill & Green – Fahrer-Terminal',
      newsletter: 'Grill & Green – Newsletter',
      table:      'Grill & Green – Bestellen am Tisch',
      schedule:   'Grill & Green – Dienstplan'
    };
    if (titleMap[view]) document.title = titleMap[view];
  } catch (_) { /* ignore */ }
  const navC = document.getElementById('nav-customer');
  const navA = document.getElementById('nav-admin');
  if (navC) navC.classList.toggle('active', view === 'customer');
  if (navA) navA.classList.toggle('active', view === 'admin');
  // Display-Lifecycle (KDS/Bar/Delivery/Fahrer-Terminal): erst ALLE verlassen,
  // dann die Ziel-View betreten. exit* ist idempotent (guardet auf die *-active-
  // body-Klasse) → für nicht-aktive Views ein No-Op. S209: driver ergänzt.
  const DISPLAY_ENTER = {
    kds: 'enterKdsView', bar: 'enterBarView',
    delivery: 'enterDeliveryView', driver: 'enterDriverView'
  };
  callIfFn('exitKdsView');
  callIfFn('exitBarView');
  callIfFn('exitDeliveryView');
  callIfFn('exitDriverView');
  if (DISPLAY_ENTER[view]) callIfFn(DISPLAY_ENTER[view]);
  if (view === 'admin') {
    const navSwitch = document.getElementById('nav-switch');
    if (navSwitch) navSwitch.style.display = '';
    if (window.App && window.App.auth && window.App.auth.isAuthenticated()) {
      showAdminPanel();
    } else {
      const loginEl = document.getElementById('admin-login');
      const panelEl = document.getElementById('admin-panel');
      if (loginEl) loginEl.style.display = 'block';
      if (panelEl) panelEl.style.display = 'none';
    }
  } else if (view === 'customer') {
    callIfFn('resetCustomerForm');
  }

  // Phase 23 (Session 22 Nachschlag): View ist jetzt sichtbar — Live-View-
  // Registry abräumen, dirty markierte Tab-Renderer feuern. Wichtig: emit
  // NACH dem ganzen display-Umschalten, damit die offsetParent-Sichtbarkeits-
  // Prüfung im Registry korrekt evaluiert.
  if (window.App && window.App.events) {
    try { window.App.events.emit('view:became-visible', { view }); }
    catch (_) { /* tolerant */ }
  }
}

export function showAdminPanel() {
  const loginEl = document.getElementById('admin-login');
  const panelEl = document.getElementById('admin-panel');
  const wasVisible = panelEl && panelEl.style.display === 'block';
  if (loginEl) loginEl.style.display = 'none';
  if (panelEl) panelEl.style.display = 'block';
  // S168 — Darkmode ist global auf :root (kein data-theme/cockpit-dark-Toggle
  // mehr): Cockpit, Gast-Seiten, Login und Modals teilen sich eine feste dunkle
  // Palette. Die Bon/Z-Bon-Karte (.receipt) bleibt die einzige helle Insel.

  // Hybrid-Shell (Topbar mit Gruppen + Sub-Tab-Bar) initialisieren —
  // idempotent: erster Aufruf mountet, weitere refreshen nur State.
  if (typeof window.initAdminShell === 'function') {
    window.initAdminShell();
  }

  // Beim Erst-Anzeigen den im Hash kodierten Tab öffnen — sonst Dashboard.
  // Token-Refresh und Hashchange-Events bleiben destruktiv-frei.
  if (!wasVisible) {
    let initialTab = 'dashboard';
    try {
      const hash = window.location.hash || '';
      const m1 = hash.match(/^#admin\/([^/]+)\/([^/]+)/); // neue Form
      const m2 = !m1 && hash.match(/^#admin\/([^/]+)/);   // Legacy
      if (m1) initialTab = m2 ? m2[1] : m1[2];
      else if (m2) initialTab = m2[1];
    } catch (_) { /* ignore */ }
    switchTab(initialTab);
  } else {
    // Phase 23 (Session 22 Nachschlag): Auch beim Re-Show (z.B. zurück aus
    // KDS/Bar) den aktuell aktiven Subtab refreshen, damit Cache-Patches die
    // während des KDS/Bar-Modus per Realtime kamen, sichtbar werden. Das
    // view:became-visible-Event aus switchView triggert die Live-View-
    // Registry — die rendert nur dirty-markierte Tabs, also kein
    // destruktiver Vollabriss. Genau das, was wir brauchen.
  }
}

/**
 * Wechselt zum Admin-Tab und ruft die jeweilige Render-Funktion auf.
 * Die Render-Funktionen leben aktuell noch teilweise in app.js — wir
 * lookup'en sie via `window.*` (saubere Lösung kommt mit den Tab-Modulen
 * in Phase 1.7).
 */
export function switchTab(tab) {
  // S188-U — Speisekarten-Konsolidierung: das Studio zog von „Ware → Rubriken"
  // (categories) unter „System → Speisekarte" (menu-Hub). Alte Klicks/Deep-Links
  // sauber auf den Hub umbiegen und dort die Studio-Unteransicht vorwählen —
  // kein toter Tab, kein Leer-Render.
  if (tab === 'categories') {
    tab = 'menu';
    if (typeof window.menuHubSetView === 'function') window.menuHubSetView('studio');
  }
  // S189 — 'food-cost' ist kein eigener Tab mehr; der Report lebt in Auswertung →
  // Statistik → Warenwirtschaft. Alte Klicks/Deep-Links auf die Statistik umbiegen.
  if (tab === 'food-cost') tab = 'stats';
  const map = {
    dashboard:    'renderDashboard',
    // S108 — `reservations` ist kein routebarer Tab mehr: die Listen-Sicht wurde
    // abgeschafft, Reservierungen laufen ausschließlich über das Dashboard
    // (Karten + „Als Nächstes" → openReservationDetail-Modal).
    orders:       'renderOrders',
    'day-close':  'loadAndRenderDayClose',
    occupancy:    'renderOccupancy',
    // S263 — Dienstplan-Maske (eigener Topbar-Reiter „Dienstplan", #admin/schedule/plan).
    plan:         'renderSchedulePlan',
    layout:       'renderLayout',
    menu:         'renderMenuEditor',
    deals:        'renderDealsAdmin',
    discounts:    'renderDiscountsAdmin',
    // S188-U — 'categories' (Studio) ist kein eigener Tab mehr; es lebt jetzt als
    // Unter-Ansicht des Speisekarte-Hubs ('menu'). Der Alias unten leitet alte
    // Klicks/Deep-Links dorthin. Kein eigener map-Eintrag mehr.
    ingredients:  'renderIngredientsAdmin',
    // S189 — 'food-cost' entfernt (Report jetzt in Statistik → Warenwirtschaft; Alias oben → stats).
    lager:        'renderStockAdmin',
    suppliers:    'renderSuppliersAdmin',
    'purchase-orders': 'renderPurchaseOrdersAdmin',
    shrinkage:    'renderShrinkageAdmin',
    lots:         'renderLotsAdmin',
    stats:        'renderStats',
    archive:      'renderArchive',
    settings:     'renderSettings'
  };
  // S110 — Dead-Tab-Guard: ein unbekannter/abgeschaffter Tab (z.B. der alte
  // Deep-Link `#admin/operations/reservations` nach S108) würde sonst auf einer
  // leeren Seite landen (keine Section, kein Renderer). → Dashboard-Fallback.
  // Greift für alle Eintrittspfade (Deep-Link-Boot, hashchange, Klicks).
  if (!Object.prototype.hasOwnProperty.call(map, tab)) tab = 'dashboard';
  // S131 (Phase D) / S167 — Operator-Routen-Guard: Deep-Links / Klicks auf einen
  // Tab, den die aktive Rolle nicht sehen darf (Gruppe ODER Sub-Tab-Restriktion,
  // z.B. „Lager" außerhalb von Ware/Lager-Bestellungen-Schwund-Chargen), landen
  // kontrolliert auf dem ersten für die Rolle sichtbaren Tab. Für die meisten
  // Rollen ist das „dashboard" (today); „Lager" sieht kein today → erster Tab =
  // „lager". Greift nur, wenn der Operator-Layer geladen ist (sonst Server hart).
  try {
    if (typeof window.isAdminTabVisible === 'function' && !window.isAdminTabVisible(tab)) {
      tab = (typeof window.firstVisibleAdminTab === 'function')
        ? window.firstVisibleAdminTab()
        : 'dashboard';
    }
  } catch (_) { /* tolerant — im Zweifel rendert der Tab, Server gated hart */ }
  // Legacy-Tabs (in admin-tabs zwischengelagert, falls Shell noch nicht geladen)
  document.querySelectorAll('.admin-tabs button[data-tab]').forEach(b => {
    b.classList.toggle('active', b.dataset.tab === tab);
  });
  document.querySelectorAll('.tab-content').forEach(s => s.style.display = 'none');
  const el = document.getElementById('tab-' + tab);
  if (el) el.style.display = 'block';
  const fnName = map[tab];
  if (fnName && typeof window[fnName] === 'function') window[fnName]();
  // Shell-State synchronisieren (animated Active-Indicator + Hash-Update)
  if (typeof window.updateShellActiveTab === 'function') {
    window.updateShellActiveTab(tab);
  }
  // Phase 23 (Session 22 Nachschlag): Tab ist jetzt sichtbar — Live-View-
  // Registry alarmieren, damit auch andere live-Tabs (z.B. wenn Realtime
  // während Tab-Wechsel kam) ihren dirty-Render nachholen.
  if (window.App && window.App.events) {
    try { window.App.events.emit('view:became-visible', { tab }); }
    catch (_) { /* tolerant */ }
  }
}

/** Cancel-Link hat Vorrang vor #admin. */
export function checkAdminAccessFromUrl() {
  if (typeof window.checkCancelRequestFromUrl === 'function' &&
      window.checkCancelRequestFromUrl()) return;
  const isAuthed = window.App && window.App.auth && window.App.auth.isAuthenticated();
  const hash = window.location.hash || '';
  // KDS-Hash kann allein (#kds) oder kombiniert (#admin#kds) auftreten —
  // beide Varianten ins KDS routen, sobald der User authenticated ist.
  if (hash.includes('#kds')) {
    switchView(isAuthed ? 'kds' : 'admin');
    return;
  }
  if (hash.includes('#bar')) {
    switchView(isAuthed ? 'bar' : 'admin');
    return;
  }
  if (hash.includes('#delivery')) {
    switchView(isAuthed ? 'delivery' : 'admin');
    return;
  }
  if (hash.includes('#driver')) {
    switchView(isAuthed ? 'driver' : 'admin');
    return;
  }
  if (hash === '#admin' || isAuthed) {
    switchView('admin');
  }
}

/**
 * Cmd/Ctrl+Shift+A: Admin-View.
 * Cmd/Ctrl+Shift+K: KDS-View.
 * Cmd/Ctrl+Shift+B: Bar-View (Theken-Display).
 * Cmd/Ctrl+Shift+L: Delivery-View (Liefer-Cockpit, S62).
 * Cmd/Ctrl+Shift+D: Fahrer-Terminal (S209).
 */
export function bindAdminShortcut() {
  document.addEventListener('keydown', e => {
    if (!(e.ctrlKey || e.metaKey) || !e.shiftKey) return;
    const k = e.key.toLowerCase();
    if (k === 'a') { e.preventDefault(); switchView('admin'); }
    else if (k === 'k') { e.preventDefault(); switchView('kds'); }
    else if (k === 'b') { e.preventDefault(); switchView('bar'); }
    else if (k === 'l') { e.preventDefault(); switchView('delivery'); }
    else if (k === 'd') { e.preventDefault(); switchView('driver'); }
  });
  window.addEventListener('hashchange', checkAdminAccessFromUrl);
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    switchView,
    switchTab,
    showAdminPanel,
    checkAdminAccessFromUrl,
    bindAdminShortcut
  });
}
